import {
    EuiButton,
    EuiButtonEmpty,
    EuiFlexGroup,
    EuiFlexItem,
    EuiForm,
    EuiSpacer,
} from '@elastic/eui'
import { LoginRequest, TokenModel, getMsalRequest } from '@services/auth'
import { PasswordField, TextField } from '@components/form'
import { appApi, useLoginMutation } from '@services/api'
import { logout, storeAzureAdToken, storeToken } from '@store/auth'
import { object, string } from 'yup'
import { useEffect, useState } from 'react'

import { AuthPage } from '@components/layout'
import AzureIcon from '../../assets/azure-icon.svg'
import BgImg3 from '@assets/bg-3.jpg'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import Logo from '@assets/logo_large.png'
import i18n from '@locale/i18n'
import { useAppDispatch } from '@hooks/store'
import { useFormik } from 'formik'
import { useMsal } from '@azure/msal-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const loginSchema = object({
    username: string().required(),
    password: string().required(),
})

const LoginPage = () => {
    const { t } = useTranslation(['auth'])
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { instance } = useMsal()

    const [token, setToken] = useState<TokenModel | undefined>(undefined)
    const [isAzureLoading, setIsAzureLoading] = useState<boolean>(false)
    const [useAzureLogin, setUseAzureLogin] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
        undefined
    )
    const [login, { isLoading: isLoginLoading, isError: isLoginError }] =
        useLoginMutation()

    const onLogin = async (data: LoginRequest) => {
        setErrorMessage(undefined)
        const token = await login(data).unwrap()
        setToken(token)
    }

    const onAzureLogin = async () => {
        setIsAzureLoading(true)
        setErrorMessage(undefined)

        try {
            const result = await instance.loginPopup(getMsalRequest())

            dispatch(storeAzureAdToken(result.accessToken))
            setUseAzureLogin(true)
        } catch (e) {
            console.error(e)
            dispatch(logout())
            setIsAzureLoading(false)
            setUseAzureLogin(false)
            setErrorMessage(i18n.t('errors:unauthorized_message'))
        }
    }

    const handleAzureLogin = async () => {
        const { data, error, isSuccess } = await dispatch(
            appApi.endpoints.azureLogin.initiate(undefined, {
                subscribe: false,
                forceRefetch: true,
            })
        )

        if (isSuccess && data) setToken(data)
        else handleErrorResponse(error)

        setUseAzureLogin(false)
        setIsAzureLoading(false)
    }

    const handleErrorResponse = (error: any) => {
        const errorData = (error as FetchBaseQueryError)?.data as any
        if (!errorData) return

        switch (errorData.status) {
            case 401:
            case 403:
                setErrorMessage(i18n.t('errors:unauthorized_message'))
                break
            case 404:
                setErrorMessage(i18n.t('errors:not_found_message'))
                break
            default:
                if (errorData.message) setErrorMessage(errorData.message)
                else setErrorMessage(i18n.t('errors:server_error_message'))
                break
        }
    }

    const form = useFormik({
        initialValues: {
            username: '',
            password: '',
        },
        validationSchema: loginSchema,
        onSubmit: onLogin,
    })

    useEffect(() => {
        if (useAzureLogin) handleAzureLogin()
    }, [useAzureLogin])

    useEffect(() => {
        if (!token) return

        dispatch(storeToken(token))
        navigate('/', { replace: true })
    }, [token])

    useEffect(() => {
        if (isLoginError && form.touched)
            setErrorMessage(t('auth:invalid_credentials'))
        else setErrorMessage(undefined)
    }, [isLoginError])

    return (
        <AuthPage
            img={Logo}
            bgImg={BgImg3}
            error={!!errorMessage}
            errorMessage={errorMessage}
        >
            <EuiForm component="form" onSubmit={form.handleSubmit}>
                <TextField
                    form={form}
                    name="username"
                    placeholder={t('auth:username')}
                />
                <PasswordField
                    form={form}
                    name="password"
                    type="dual"
                    placeholder={t('auth:password')}
                />
                <EuiSpacer size="l" />
                <EuiFlexGroup justifyContent="spaceBetween" alignItems="center">
                    <EuiFlexItem grow={2}>
                        <EuiButton
                            fill
                            type="submit"
                            fullWidth
                            isLoading={isLoginLoading}
                        >
                            {t('auth:login')}
                        </EuiButton>
                    </EuiFlexItem>
                    <EuiFlexItem grow={1}>
                        <EuiButtonEmpty size="xs" flush="right">
                            {t('auth:forgot_password')}
                        </EuiButtonEmpty>
                    </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer size="m" />
                <EuiButton
                    iconType={AzureIcon}
                    fullWidth
                    onClick={onAzureLogin}
                    isLoading={isAzureLoading}
                >
                    {t('auth:login_with_azure')}
                </EuiButton>
            </EuiForm>
        </AuthPage>
    )
}

export default LoginPage
